import { Income } from '../income';
import { DocumentMeta } from '../meta';
import { RightNote } from '../right-note';
import { RightType } from '../static/types';
import { toFixed, toFixedPercentage } from '../utils';
import { ActionName, IncomeAction } from './action';
import {
  Condition,
  ConditionGroup,
  ConditionWithTarget,
  getTakeFromUntilCondition,
  isCondition,
  isConditionWithTarget,
  and,
  checkCondition,
  skipTakeFromUntilConditions,
  ConditionResult,
  isPoolCondition,
  isFilmAmortizedCondition,
  isConditionEqual
} from './conditions';
import { nodeExists, getNode, isVerticalGroup, isRight, getTransferDetails, NodeType } from './node';
import { RightState, TitleState } from './state';
import { WaterfallContract, Version, WaterfallSource, Waterfall } from './waterfall';
import { sum } from '../utils';
import { Amortization } from './amortization';

export interface Right {
  _meta?: DocumentMeta;
  id: string;
  type: RightType,
  name: string;
  contractId?: string;
  actionName: ActionName;
  groupId: string;
  order: number; // Used for vertical group childs (steps), set to zero for others
  /** Parents (above) of this right */
  nextIds: string[];
  rightholderId: string;
  percent: number; // Between 0 and 100
  conditions?: ConditionGroup;
  version: Record<string, RightVersion>;
  blameId?: string;
  pools: string[]; // Should not be used for groups (horizontal or vertical) (removed in #11002)
  collapsed: Record<string, boolean>; // Used for horizontal group collapsed state. Key is current user id.
  notes?: RightNote[]; // #10602 Why this entitlement exists, by a user or by AI Magic Import. Never read by the engine.
}

export interface RightWithPercentConfig extends Right {
  percentConfig: PercentConfig;
}

export interface RightVersion {
  standalone?: true;
  percent?: number;
  conditions?: ConditionGroup;
}

export interface RightOverride {
  incomeId: string;
  rightId: string;
  percent: number;
  initial: number;
  comment: string;
}

export type RightGroups = {
  internal: Right[]; // Rights that are part of the statement
  enabled: Right[]; // Rights that are enabled (contracts)
  all: Right[]; // All rights
}

export function createRight(params: Partial<Right> = {}) {
  const right: Right = {
    id: '',
    type: 'unknown',
    name: '',
    actionName: 'prepend',
    groupId: '',
    order: 0,
    nextIds: [],
    rightholderId: '',
    percent: 100,
    pools: [],
    version: {},
    collapsed: {},
    ...params
  }

  if (!right.name) right.name = right.id;

  return right;
}

export function createRightOverride(params: Partial<RightOverride> = {}): RightOverride {
  return {
    incomeId: '',
    rightId: '',
    percent: 100,
    initial: 100,
    comment: '',
    ...params
  }
}

export function convertRightsTo(rights: Right[], version: Version, defaultVersionId: string) {
  if (!version?.id || !defaultVersionId) return rights;
  if (version.standalone) return rights.filter(r => r.version[version.id]);
  return rights.filter(r => !Object.values(r.version).some(v => v.standalone)).map(r => {
    if (!r.version) r.version = {};
    if (!r.version[defaultVersionId] || Object.keys(r.version[defaultVersionId]).length === 0) r.version[defaultVersionId] = { conditions: r.conditions, percent: r.percent };
    if (!r.version[version.id] || Object.keys(r.version[version.id]).length === 0) r.version[version.id] = { conditions: r.conditions, percent: r.percent };
    const rightVersion = r.version[version.id];
    if (rightVersion?.conditions) r.conditions = rightVersion.conditions;
    if (rightVersion?.percent !== undefined) r.percent = rightVersion.percent;
    return r;
  });
}

/**
 * @dev get right percentage for a given income using two states.
 * This is used to calculate revenu for a given right when it is combined with another version.
 * Example: rightA is configured to take same amount in versionB as in versionA
 */
export function getCombinedPercent(rightId: string, income: Income, state: TitleState, targetedState: TitleState) {
  const transfers = Object.values(state.transfers).filter(t => t.to === rightId && t.history.some(h => h.incomeId === income.id));
  if (transfers.length > 1) throw new Error('Multiple transfers found');
  const transfer = transfers[0];
  if (!transfer) return 0;

  const intermediateDetails = getTransferDetails([income.id], income.sourceId, transfer.from, transfer.to, state);
  const targetedDetails = getTransferDetails([income.id], income.sourceId, transfer.from, transfer.to, targetedState);
  return targetedDetails.taken / intermediateDetails.max;
}

/**
 * Order rights by their nextIds.
 * For waterfall rights creation, we need them ordered (prepend)
 * @param rights 
 * @param throwError 
 * @returns 
 */
export function orderRights(rights: Right[], throwError = true): Right[] {
  const allNextIds = Array.from(new Set(rights.map(right => right.nextIds).flat()));
  const notMatching = allNextIds.filter(id => !rights.map(right => right.id).includes(id));
  if (notMatching.length) {
    const message = `Some nextIds do not match any right id : ${notMatching}`;
    if (throwError) throw new Error(message);
    console.warn(message);
    return [];
  }

  const rootRights = rights.filter(right => !right.nextIds.length);
  let childRights = rights.filter(right => right.nextIds.length);
  const orderedRights = rootRights;

  while (childRights.length > 0) {
    const nextRights = childRights.filter(r => r.nextIds.every(nextId => orderedRights.map(i => i.id).includes(nextId)));
    childRights = childRights.filter(r => !nextRights.includes(r));
    orderedRights.push(...nextRights);
  }

  return orderedRights;
}

export function getRightConditions(right: Right | RightState | RightVersion) {
  return (right?.conditions?.conditions?.filter(c => isCondition(c)).filter(c => !!c) || []) as Condition[];
}

export function sanitizeConditions(right: Right, rights: Right[]) {
  const conditions = getRightConditions(right).filter(c => isConditionValid(c, rights, right));
  return and(conditions);
}

export function hasValidConditions(right: Right, rights: Right[]) {
  return getRightConditions(right).every(c => isConditionValid(c, rights, right));
}

// TODO #10938 #10874 add more checks here
export function isConditionValid(condition: Condition, rights: Right[], subject?: Right) {
  let error = '';
  switch (condition.name) {
    case 'rightTurnover':
    case 'rightRevenu':
      if (!rights.find(r => r.id === condition.payload.rightId)) error = `${subject?.name} (id: ${subject?.id}) has an invalid condition ${condition.name} on right "${condition.payload.rightId}"`;
      break;
    case 'groupRevenu':
    case 'groupTurnover':
      if (!rights.find(r => r.id === condition.payload.groupId)) error = `${subject?.name} (id: ${subject?.id}) has an invalid condition ${condition.name} on group "${condition.payload.groupId}"`;
      break;
    case 'orgRevenu':
    case 'orgTurnover':
      if (!rights.find(r => r.rightholderId === condition.payload.orgId)) error = `${subject?.name} (id: ${subject?.id}) has an invalid condition ${condition.name} on org "${condition.payload.orgId}"`;
      break;
    default:
      // Not handled
      break;
  }

  if (error && subject) console.error(error);
  return !error;
}

export const groupRightTypes: (RightType | NodeType)[] = ['horizontal', 'vertical'];
export function skipGroups(rights: Right[]) {
  // Groups are skipped here and revenue will be re-calculated from the childrens
  return rights.filter(r => !groupRightTypes.includes(r.type));
}

/**
 * Return current right and its childs (excluding groups but with groups members)
 * @param rightId
 * @param rights 
 */
export function getChilds(rightId: string, rights: Right[]): Right[] {
  const right = rights.find(r => r.id === rightId);
  if (!right) return [];
  const _childs = [..._getChilds(rightId, rights), right];
  const childs: Right[] = [];
  for (const child of _childs) {
    if (isGroup(child)) {
      const subChilds = rights.filter(r => r.groupId === child.id);
      for (const schild of subChilds) {
        if (isGroup(schild)) {
          childs.push(...rights.filter(r => r.groupId === schild.id));
        } else {
          childs.push(schild);
        }
      }
    } else {
      childs.push(child);
    }
  }

  const uniqueChilds = Array.from(new Set(childs.map(c => c.id))).map(id => childs.find(c => c.id === id));
  return uniqueChilds.filter(c => !!c);
}

/**
 * Get the root group of a right or the right itself if it does not have a group
 * @param right 
 * @param rights 
 * @returns 
 */
export function getRootGroup(right: Right, rights: Right[]): Right {
  if (!right.groupId) return right; // Does not have a group
  const group = rights.find(r => r.id === right.groupId);
  if (!group) throw new Error(`Group ${right.groupId} not found`);
  if (!group.groupId) return group; // Group does not have a group
  const rootGroup = rights.find(r => r.id === group.groupId);
  if (!rootGroup) throw new Error(`Root group ${group.groupId} not found`);
  return rootGroup;
}

export function getSingleSourceChildIds(sourceId: string, rights: Right[], sources: WaterfallSource[]) {
  const source = sources.find(s => s.id === sourceId);
  if (!source) return [];
  const sourceChildIds = Array.from(new Set(getChilds(source.destinationId, rights).map(c => c.id)));

  const otherSources = sources.filter(s => s.id !== sourceId);
  const otherTopLevelRigthIds = otherSources.map(s => s.destinationId);
  const otherChildIds = Array.from(new Set(otherTopLevelRigthIds.map(id => getChilds(id, rights).map(c => c.id)).flat()));
  return sourceChildIds.filter(id => !otherChildIds.includes(id));
}

export function isLinkedToSource(rightId: string, rights: Right[], sourceId: string, sources: WaterfallSource[]) {
  const source = sources.find(s => s.id === sourceId);
  if (!source) return false;
  const sourceChildIds = Array.from(new Set(getChilds(source.destinationId, rights).map(c => c.id)));
  return sourceChildIds.includes(rightId);
}

/**
 * Get rights to display on graph for a rightholder that is not admin:
 * Includes all rights that belongs to the rightholder and their parents includind group childs.
 * Does not include root rights of a an horizontal group that does not belong to the rightholder.
 * @param rightholderId 
 * @param _rights 
 * @returns 
 */
export function getRightsToDisplay(rightholderId: string, _rights: Right[]): Right[] {
  if (!rightholderId) return [];
  const rights = _rights.filter(r => r.rightholderId === rightholderId);
  const rightsToDisplay: Right[] = [];
  for (const right of rights) {
    const parents = getParents(right.id, _rights);
    if (right.groupId) {
      const group = _rights.find(r => r.id === right.groupId);
      parents.push(group);
      const childs = _rights.filter(r => r.groupId === group.id && r.rightholderId === right.rightholderId);
      parents.push(...childs);

      if (group.groupId) {
        const subGroup = _rights.find(r => r.id === group.groupId);
        parents.push(subGroup);
        const subChilds = _rights.filter(r => r.groupId === subGroup.id && r.rightholderId === right.rightholderId);
        parents.push(...subChilds);
      }
    } else {
      rightsToDisplay.push(right);
    }

    for (const parent of parents) {
      if (!rightsToDisplay.find(r => r.id === parent.id)) {
        rightsToDisplay.push(parent);
      }
    }
  }

  return rightsToDisplay;
}

function getParents(rightId: string, rights: Right[]): Right[] {
  const right = rights.find(r => r.id === rightId);
  if (!right) return [];
  let parents: Right[] = [];
  if (right.groupId) {
    const group = rights.find(r => r.id === right.groupId);
    if (group.groupId) {
      const subGroup = rights.find(r => r.id === group.groupId);
      parents = _getParents(subGroup, rights);
    } else {
      parents = _getParents(group, rights);
    }
  } else {
    parents = _getParents(right, rights);
  }

  const parentIds = Array.from(new Set(parents.map(p => p.id)));
  return parentIds.map(id => parents.find(p => p.id === id));
}

function _getParents(right: Right, rights: Right[]): Right[] {
  if (!right) return [];
  const parents = right.nextIds.map(id => rights.find(r => r.id === id));
  if (!parents.length) return [];
  const parentGroups = parents.filter(p => isGroup(p));
  for (const group of parentGroups) {
    const childs = rights.filter(r => r.groupId === group.id);
    parents.push(...childs);
    const subGroups = childs.filter(c => isGroup(c));
    for (const subGroup of subGroups) {
      parents.push(...rights.filter(r => r.groupId === subGroup.id));
    }
  }
  return parents.concat(parents.map(p => _getParents(p, rights)).flat());
}

function isGroup(right?: Right): boolean {
  if (!right) return false;
  return groupRightTypes.includes(right.type);
}

function _getChilds(rightId: string, rights: Right[]): Right[] {
  if (!rightId) return [];
  const childs = rights.filter(r => r.nextIds.includes(rightId));
  if (!childs.length) return [];
  return childs.concat(childs.map(c => getChilds(c.id, rights)).flat());
}

/**
 * Return expense types defined in conditions of a right
 * @param right 
 * @returns 
 */
export function getRightExpenseTypes(right: Right) {
  const conditions = getRightConditions(right);
  const conditionsWithTarget = conditions.filter(c => isConditionWithTarget(c)) as ConditionWithTarget[];

  const expenseTargets = conditionsWithTarget
    .map(c => (typeof c.payload.target === 'object' && c.payload.target.in === 'expense') ? c.payload.target.id : undefined)
    .filter(id => !!id);

  return Array.from(new Set(expenseTargets));
}

export function getRightsExpenseTypes(rights: Right[]) {
  return rights.map(r => getRightExpenseTypes(r)).flat();
}

export function copyVerticalConditions(createdId: string, duplicatedId: string, newRights: Right[], existingRights: Right[]) {
  const subMembers = newRights.filter(r => r.groupId === createdId);
  const duplicatedSubMembers = existingRights.filter(r => r.groupId === duplicatedId);
  subMembers.forEach(subMember => {
    const newSubMember = newRights.find(r => r.id === subMember.id);
    const duplicatedSubMember = duplicatedSubMembers.find(r => r.order === subMember.order);
    if (duplicatedSubMember) {
      newSubMember.conditions = duplicatedSubMember.conditions;
      newSubMember.version = duplicatedSubMember.version;
    }
  });
}

export function copyRightConditions(createdId: string, duplicatedId: string, newRights: Right[], existingRights: Right[]) {
  const newRight = newRights.find(r => r.id === createdId);
  const duplicatedRight = existingRights.find(r => r.id === duplicatedId);
  if (duplicatedRight) {
    newRight.conditions = duplicatedRight.conditions;
    newRight.version = duplicatedRight.version;
  }
}

export function copyHorizontalConditions(createdId: string, duplicatedId: string, newRights: Right[], existingRights: Right[], copyOf: string) {
  const members = newRights.filter(r => r.groupId === createdId);
  const duplicatedMembers = existingRights.filter(r => r.groupId === duplicatedId);
  members.forEach(member => {
    const newMember = newRights.find(r => r.id === member.id);
    if (newMember.type === 'vertical') {
      const duplicatedMember = duplicatedMembers.find(r => r.name === member.name.replace(copyOf, ''));
      if (duplicatedMember) {
        copyVerticalConditions(newMember.id, duplicatedMember.id, newRights, existingRights);
      }
    } else {
      const duplicatedMember = duplicatedMembers.find(r => r.name === member.name.replace(copyOf, ''));
      if (duplicatedMember) {
        newMember.conditions = duplicatedMember.conditions;
        newMember.version = duplicatedMember.version;
      }
    }
  });
}

export type PercentConfigReason = 'valid' | 'not-enabled' | 'conditions-not-met' | 'not-reached';
export interface PercentConfig {
  percent: number;
  calculatedOn: 'previous' | 'receipt' | 'shadow';
  retroactively: boolean;
  receiptId?: string;
  checked?: {
    value: boolean;
    reason: PercentConfigReason;
  };
  conditionLabel?: {
    short: string;
    full: string;
  };
  conditionCount?: number;
  combinedWith?: Version;
}

export function getPercentConfig(right: Right, conditionResult?: ConditionResult, precision = 100): PercentConfig {
  const cond = getTakeFromUntilCondition(getRightConditions(right));
  const original = right?.percent || 0;
  const percentConfig: PercentConfig = {
    percent: 0,
    calculatedOn: cond ? 'receipt' : 'previous',
    retroactively: cond?.name === 'takefromSourceUntil' || false,
    receiptId: cond?.payload.sourceId || '',
  };

  if (cond?.payload.percent !== undefined) {
    percentConfig.percent = precision !== Infinity ? toFixedPercentage(cond.payload.percent, precision) : cond.payload.percent * 100;
  } else {
    percentConfig.percent = precision !== Infinity ? toFixed(original, precision) : original;
  }

  if (conditionResult) {
    let reason: PercentConfigReason = 'valid';
    if (!conditionResult.enabled) reason = 'not-enabled';
    else if (!conditionResult.checked) reason = 'conditions-not-met';
    percentConfig.checked = { value: conditionResult.checked, reason };
  }

  return percentConfig;
}

const dummyPayload: IncomeAction = {
  id: 'foo',
  contractId: '',
  from: 'test',
  to: '',
  amount: 0,
  date: new Date(),
  territories: [],
  medias: [],
}

export function isRightChecked(rightId: string, state: TitleState): ConditionResult {
  if (!nodeExists(state, rightId)) return { checked: false, enabled: false, shadow: false };
  const node = getNode(state, rightId);
  if (!isRight(state, node)) return { checked: false, enabled: false, shadow: false };

  const right = { ...node, conditions: and(skipTakeFromUntilConditions(getRightConditions(node))) };
  return checkCondition({ state, right, income: dummyPayload, sourceId: dummyPayload.from });
}

export function getActualHorizontalPercent(horizontalId: string, state: TitleState, rights: Right[]) {
  const members = rights.filter(r => r.groupId === horizontalId);
  const checkedIds: string[] = [];

  for (const member of members) {
    if (!nodeExists(state, member.id)) continue;
    const node = getNode(state, member.id);

    if (isVerticalGroup(state, node)) {
      const checkedId = getVerticalCheckedId(node.children, state);
      if (checkedId) checkedIds.push(checkedId);
    } else if (isRight(state, node)) {
      const right = { ...node, conditions: and(skipTakeFromUntilConditions(getRightConditions(node))) };
      const { checked } = checkCondition({ state, right, income: dummyPayload, sourceId: dummyPayload.from });
      if (checked) checkedIds.push(node.id);
    }
  }

  if (!nodeExists(state, horizontalId)) return 0;
  const currentNode = getNode(state, horizontalId);
  const checkedRights = rights.filter(r => checkedIds.includes(r.id));
  const nodePercentages = checkedRights.map(r => getPercentConfig(r, undefined, Infinity).percent);
  const totalPercent = (sum(nodePercentages) / 100) * currentNode.percent;
  return toFixedPercentage(totalPercent, 100);
}

function getVerticalCheckedId(childIds: string[], state: TitleState) {
  for (const childId of childIds) {
    if (!nodeExists(state, childId)) continue;
    const childNode = getNode(state, childId);
    if (isRight(state, childNode)) {
      const right = { ...childNode, conditions: and(skipTakeFromUntilConditions(getRightConditions(childNode))) };
      const { checked } = checkCondition({ state, right, income: dummyPayload, sourceId: dummyPayload.from });
      if (checked) return childId;
    }
  }
}

export function getActualVerticalPercent(verticalId: string, state: TitleState, rights: Right[]) {
  if (!nodeExists(state, verticalId)) return 0;
  const members = rights.filter(r => r.groupId === verticalId);
  const checkedId = getVerticalCheckedId(members.map(m => m.id), state);
  const checkedRight = rights.find(r => r.id === checkedId);
  return getPercentConfig(checkedRight, undefined, Infinity).percent;
}

export function getPoolMembersToHighlight(rightId: string, rights: Right[]): string[] {
  const right = rights.find(right => right.id === rightId.replace('__pools__', ''));
  const highlightedRights = rights.filter(r => r.pools.some(p => right.pools.includes(p)));
  const highlightedRightIds = highlightedRights.map(r => r.id);
  highlightedRightIds.push(...highlightBrothers(highlightedRights, rights));
  return Array.from(new Set(highlightedRightIds));
}

export function highlightBrothers(highlightedRights: Right[], rights: Right[]) {
  const highlightedRightIds: string[] = [];
  for (const highlightedRight of highlightedRights) {
    const group = highlightedRight.groupId ? rights.find(r => r.id === highlightedRight.groupId) : undefined;
    // Highlight all members of the vertical group
    if (group?.type === 'vertical') {
      const members = rights.filter(r => r.groupId === group.id);
      highlightedRightIds.push(...members.map(r => r.id));
    }
  }

  return Array.from(new Set(highlightedRightIds));
}

export function getEnabledRights(rights: Right[], contracts: WaterfallContract[], date: Date) {
  return rights.filter(r => {
    if (!r.contractId) return true;
    // Filters not enabled rights at the given date
    const contract = contracts.find(c => c.id === r.contractId);
    if (!contract) return false;
    /** @dev see rightEnabled logic in conditions */
    return date >= contract.duration.from && date <= contract.duration.to;
  });
}

export function getMembers(groupId: string, rights: Right[], enabledRights: Right[]): Right[] {
  const group = rights.find(r => r.id === groupId);
  if (!group || !groupRightTypes.includes(group.type)) return [];
  if (group.type === 'vertical') return enabledRights.filter(r => r.groupId === groupId);
  if (group.type === 'horizontal') return enabledRights.filter(r => r.groupId === groupId).map(m => m.type === 'vertical' ? enabledRights.filter(r => r.groupId === m.id) : [m]).flat();
}

/**
 * Get all rights linked to a pool (directly or through conditions)
 * @param rights 
 * @param pool 
 * @param amortizations 
 * @returns 
 */
export function getRightsLinkedToPool(rights: Right[], pool: string, amortizations: Amortization[] = []) {
  const amortizationsLinkedToPool = amortizations.filter(a => a.poolName === pool);
  const rightIdsLinkedToPool = rights.filter(r => r.pools.includes(pool)).map(r => r.id);

  const isConditionOnPool = (c: Condition) => isPoolCondition(c) && c.payload.pool === pool;
  const isConditionOnFilmAmortized = (c: Condition) => isFilmAmortizedCondition(c) && amortizationsLinkedToPool.some(a => typeof c.payload.target !== 'number' && c.payload.target.id === a.id);
  const rightIdsWithPoolConditions = rights
    .filter(r => {
      if (getRightConditions(r).filter(c => isConditionOnPool(c) || isConditionOnFilmAmortized(c)).length > 0) return true;
      for (const version in r.version || {}) {
        if (getRightConditions(r.version[version]).filter(c => isConditionOnPool(c) || isConditionOnFilmAmortized(c)).length > 0) return true;
      }
      return false;
    })
    .filter(r => !!r)
    .map(r => r.id);

  const rightIds = Array.from(new Set([...rightIdsLinkedToPool, ...rightIdsWithPoolConditions]));
  return rights.filter(r => rightIds.includes(r.id));
}

/**
 * Remove a pool from all rights (directly or through conditions)
 * @param rights 
 * @param pool 
 * @param amortizations 
 * @returns 
 */
export function removePoolFromRights(rights: Right[], pool: string, amortizations: Amortization[]): Right[] {
  const amortizationsLinkedToPool = amortizations.filter(a => a.poolName === pool);

  const isNotLinkedToPool = (c: Condition) => isPoolCondition(c) ? c.payload.pool !== pool : true;
  const isNotLinkedToAmortization = (c: Condition) => isFilmAmortizedCondition(c) ? amortizationsLinkedToPool.some(a => typeof c.payload.target !== 'number' && c.payload.target.id !== a.id) : true;

  return rights.map(r => {
    // Remove pool condition from all versions
    // TODO #10874 conditions should not be removed but marked as "errored"
    for (const version in r.version || {}) {
      if (r.version[version]?.conditions?.conditions) {
        r.version[version].conditions.conditions = getRightConditions(r.version[version]).filter(c => isNotLinkedToPool(c)).filter(c => isNotLinkedToAmortization(c));
      }
    }
    const updatedConditions = getRightConditions(r).filter(c => isNotLinkedToPool(c)).filter(c => isNotLinkedToAmortization(c));
    return { ...r, conditions: { ...r.conditions, conditions: updatedConditions }, pools: r.pools.filter(p => p !== pool), version: r.version };
  });
}

/**
 * Update a pool in all rights (directly or through conditions)
 * @param poolMembers 
 * @param _rights 
 * @param oldName 
 * @param newName 
 * @returns 
 */
export function updatePoolInRights(poolMembers: string[], _rights: Right[], oldName: string, newName: string) {
  const rights = _rights.map(r => ({ ...r, pools: r.pools.filter(p => p !== oldName) }));
  rights.forEach(r => {
    if (poolMembers.includes(r.id)) r.pools.push(newName);
    const conditions = getRightConditions(r).map(c => isPoolCondition(c) && c.payload.pool === oldName ? { ...c, payload: { ...c.payload, pool: newName } } : c);
    r.conditions = { ...r.conditions, conditions };

    for (const version in r.version || {}) {
      if (r.version[version]?.conditions?.conditions) {
        r.version[version].conditions.conditions = getRightConditions(r.version[version]).map(c => isPoolCondition(c) && c.payload.pool === oldName ? { ...c, payload: { ...c.payload, pool: newName } } : c);
      }
    }
  });

  return rights;
}

export function rightCombinedWith(right: Right, version: Version, waterfall: Waterfall) {
  if (!version?.combined) return undefined;
  if (getPercentConfig(right).calculatedOn === 'receipt') return undefined;
  const combinedWith = version.combinedWith.find(c => {
    if (c.rightIds?.includes(right?.id)) return true;
    if (c.rightholderIds?.length && c.rightholderIds.includes(right?.rightholderId)) return true;
    return false;
  });
  if (!combinedWith) return undefined;
  return waterfall.versions.find(v => v.id === combinedWith.versionId);
}

export function isIdenticalToDefaultVersion(right: Right, versionId: string, defaultVersionId: string) {
  if (!right.version[versionId] || Object.keys(right.version[versionId]).length === 0) return true;
  if (!right.version[defaultVersionId] || Object.keys(right.version[defaultVersionId]).length === 0) return true;
  const samePercent = right.version[versionId].percent === right.version[defaultVersionId].percent;
  if (!samePercent) return false;

  const versionConditions = getRightConditions(right.version[versionId]);
  const defaultVersionConditions = getRightConditions(right.version[defaultVersionId]);
  if (versionConditions.length === defaultVersionConditions.length) {
    if (versionConditions.length === 0 && defaultVersionConditions.length === 0) return true;

    return versionConditions.every(vc => defaultVersionConditions.some(dc => isConditionEqual(vc, dc)));
  }

  return false;
}